import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigation } from '../../../shared/contexts/NavigationContext';
import './FullscreenCardModal.css';

/** Событие: фул-карточка открыта; страница объекта может размонтироваться. */
export const FULLSCREEN_CARD_OPENED_EVENT = 'ryaskov-fullscreen-card-opened';
/** Событие: фул-карточка закрыта; страница может перевесить кнопку «Назад» (после всех эффектов). */
export const FULLSCREEN_CARD_CLOSED_EVENT = 'ryaskov-fullscreen-card-closed';
/** Вызывается в момент нажатия «Назад» в карточке — ObjectPage ставит коoldown, чтобы не уйти в список от двойного срабатывания. */
export const FULLSCREEN_CARD_CLOSING_BY_BACK_EVENT = 'ryaskov-fullscreen-card-closing-by-back';

interface FullscreenCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon?: string;
  children: React.ReactNode;
  headerActions?: React.ReactNode; // Дополнительные действия в заголовке (кнопки, иконки и т.д.)
}

const FullscreenCardModal: React.FC<FullscreenCardModalProps> = ({
  isOpen,
  onClose,
  title,
  icon,
  children,
  headerActions
}) => {
  const { pushBackLayer, popBackLayer } = useNavigation();
  const isClosingRef = useRef(false);
  const closedByBackRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Эмитим события при открытии/закрытии fullscreen карточки
  useEffect(() => {
    if (isOpen) {
      closedByBackRef.current = false;
      window.dispatchEvent(new CustomEvent(FULLSCREEN_CARD_OPENED_EVENT));
    } else {
      window.dispatchEvent(new CustomEvent(FULLSCREEN_CARD_CLOSED_EVENT));
    }
  }, [isOpen]);

  // В карточке кнопка «Назад» только закрывает карточку; стек в контексте сам вернёт обработчик окна объекта.
  useEffect(() => {
    if (!isOpen) {
      isClosingRef.current = false;
      if (!closedByBackRef.current) popBackLayer();
      return;
    }

    const handleCloseWithProtection = () => {
      if (isClosingRef.current) return;
      isClosingRef.current = true;
      closedByBackRef.current = true;

      window.dispatchEvent(new CustomEvent(FULLSCREEN_CARD_CLOSING_BY_BACK_EVENT));
      onCloseRef.current();
      popBackLayer();

      setTimeout(() => {
        isClosingRef.current = false;
      }, 100);
    };

    pushBackLayer(handleCloseWithProtection, false);

    return () => {
      if (!closedByBackRef.current) popBackLayer();
    };
  }, [isOpen, pushBackLayer, popBackLayer]);

  // Блокируем скролл body когда модальное окно открыто
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (isClosingRef.current) return;
        
        isClosingRef.current = true;
        onCloseRef.current();
        
        setTimeout(() => {
          isClosingRef.current = false;
        }, 1000);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);
  

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fullscreen-card-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (isClosingRef.current) return;
          
          isClosingRef.current = true;
          onCloseRef.current();
          
          setTimeout(() => {
            isClosingRef.current = false;
          }, 1000);
        }
      }}
    >
      <div className="fullscreen-card-modal">
        <div className="fullscreen-card-header">
          <div className="fullscreen-card-title">
            <h2>{title}</h2>
          </div>
          {headerActions ? (
            <div className="fullscreen-card-actions">
              {headerActions}
            </div>
          ) : null}
        </div>
        
        <div className="fullscreen-card-content-wrapper">
          <div className="fullscreen-card-content">
            {children}
          </div>
          {/* Контейнер для меню и статус-бара - они будут рендериться сюда через портал */}
          <div id="fullscreen-card-bottom-elements" className="fullscreen-card-bottom-elements"></div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FullscreenCardModal;

